import { startGoogleSignIn } from './google-auth';
import { authClient } from '@/lib/better-auth-client-actions';
import { isInvalidCredentialAuthError } from '@/lib/auth/email-not-verified';
import {
  clearPendingAuthPassword,
  persistPendingAuthPassword,
  resolvePendingAuthPassword,
} from '@/lib/auth/pending-auth-password';
import {
  clearPendingSignupPageToken,
  issuePendingSignupPageToken,
  setPendingSignupPageToken,
} from '@/lib/signup-pages';
import { toast } from 'sonner';
import { normalizeSignUpEmail } from './sign-up-email';
import type { useSignUpState } from './use-sign-up-state';
import type { SignUpFormType } from './use-sign-up-state';
import { pushAuthHandoff } from '@/lib/auth/auth-handoff';
export function createSignUpActions(state: ReturnType<typeof useSignUpState>) {
  const {
    inviteToken,
    authBasePath,
    signupPage,
    copy,
    handleAuthError,
    searchParams,
    router,
    setIsLoading,
    setPendingAction,
    setError,
    selectedLanguage,
    setLanguageSelectionDeferred,
    pendingEmail,
    pendingPassword,
    setPendingPassword,
    otpCode,
    setOtpCode,
    form,
    applyFieldErrors,
    buildSignInParams,
    buildSigningInParams,
  } = state;

  const handleSubmit = async (values: SignUpFormType) => {
    setIsLoading(true);
    setPendingAction('credentials');
    setError(null);
    form.clearErrors();

    try {
      // Store invitation token if present
      if (inviteToken) {
        localStorage.setItem('pendingInvitation', inviteToken);
      }

      // Trim and lowercase only; the schema rejects plus aliases (#4374)
      const normalizedEmail = normalizeSignUpEmail(values.email);
      const pendingSignupToken = signupPage
        ? (await issuePendingSignupPageToken(signupPage.slug)).token
        : null;

      // Optionally persist email locally for onboarding continuity
      try {
        localStorage.setItem('signup.email', normalizedEmail);
      } catch {}

      console.log('[auth:client] signup start', { email: normalizedEmail });

      // Note: we don't need a separate client-side "pending onboarding" flag.
      // `/auth/signing-in` keys onboarding off the explicit
      // `signupFlow=1` URL param that we attach to post-auth redirects.

      // Sign up with Better Auth (ensure required name is provided)
      const fullName =
        values.name.trim() || normalizedEmail.split('@')[0] || 'User';
      persistPendingAuthPassword(normalizedEmail, values.password);
      setPendingPassword(values.password);
      const result = await authClient.signUp.email({
        email: normalizedEmail,
        password: values.password,
        name: fullName,
        signupLocale: selectedLanguage,
      });

      if (result.error) {
        console.error('[auth:client] signup error', { error: result.error });
        clearPendingAuthPassword();
        setPendingPassword('');
        const { error: errorMessage, fieldErrors: newFieldErrors } =
          handleAuthError(result.error);
        if (errorMessage) {
          setError(errorMessage);
          toast.error(errorMessage);
        }
        if (Object.keys(newFieldErrors).length > 0) {
          applyFieldErrors(newFieldErrors);
          const firstError = Object.values(newFieldErrors)[0];
          if (firstError) {
            setError(firstError);
            toast.error(firstError);
          }
        }
        setIsLoading(false);
        return;
      }

      console.log('[auth:client] signup success', { email: normalizedEmail });
      if (pendingSignupToken) {
        setPendingSignupPageToken(pendingSignupToken);
      }

      // OTP will be sent server-side (sendVerificationOnSignUp)
      toast.success(copy.accountCreatedCheckEmail);
      console.log('[auth:client] signup otp-sent (server-side)', {
        email: normalizedEmail,
      });

      // Move to step 3 (check email page)
      const params = new URLSearchParams(searchParams.toString());
      params.set('step', '3');
      params.set('email', normalizedEmail);
      setLanguageSelectionDeferred(false);
      router.push(`${authBasePath}?${params.toString()}`);
    } catch (error) {
      clearPendingAuthPassword();
      setPendingPassword('');
      const { error: errorMessage, fieldErrors: newFieldErrors } =
        handleAuthError(error);
      if (errorMessage) {
        setError(errorMessage);
        toast.error(errorMessage);
      }
      if (Object.keys(newFieldErrors).length > 0) {
        applyFieldErrors(newFieldErrors);
        const firstError = Object.values(newFieldErrors)[0];
        if (firstError) {
          setError(firstError);
          toast.error(firstError);
        }
      }
      console.error('[auth:client] signup error', { error });
    } finally {
      setIsLoading(false);
      setPendingAction(null);
    }
  };

  const handleOtpVerification = async () => {
    if (!otpCode || otpCode.length !== 6) {
      setError(copy.enterSixDigitCode);
      return;
    }

    setIsLoading(true);
    setPendingAction('verify');
    setError(null);

    let handedOff = false;
    try {
      // Verify the exact address the account was created with
      const normalizedEmail = normalizeSignUpEmail(pendingEmail);

      console.log('[auth:client] signup otpSubmit start', {
        email: normalizedEmail,
      });

      const result = await authClient.emailOtp.verifyEmail({
        email: normalizedEmail,
        otp: otpCode,
      });

      if (result.error) {
        console.error('[auth:client] signup otpSubmit error', {
          error: result.error,
        });
        const { error: errorMessage } = handleAuthError(result.error);
        setError(errorMessage || copy.invalidVerificationCode);
        toast.error(errorMessage || copy.invalidVerificationCode);
        setIsLoading(false);
        return;
      }

      console.log('[auth:client] signup otpSubmit success', {
        email: normalizedEmail,
      });

      // Auto sign in after successful verification
      const passwordForSignin = resolvePendingAuthPassword({
        inMemoryPassword: pendingPassword,
        email: normalizedEmail,
      });
      if (!passwordForSignin) {
        clearPendingAuthPassword();
        toast.info(copy.emailVerifiedSignInAgain);
        const params = buildSignInParams();
        params.set('mode', 'signin');
        params.set('email', normalizedEmail);
        router.push(`${authBasePath}?${params.toString()}`);
        setIsLoading(false);
        return;
      }
      const signInResult = await authClient.signIn.email({
        email: normalizedEmail,
        password: passwordForSignin,
      });

      if (signInResult.error) {
        const { error: errorMessage } = handleAuthError(signInResult.error);
        if (isInvalidCredentialAuthError(signInResult.error)) {
          clearPendingAuthPassword();
          setPendingPassword('');
          const params = buildSignInParams();
          params.set('mode', 'signin');
          params.set('email', normalizedEmail);
          params.set('authError', 'email-verified-password-required');
          router.push(`${authBasePath}?${params.toString()}`);
          const recoveryMessage = copy.emailVerifiedEnterPassword;
          setError(recoveryMessage);
          toast.info(recoveryMessage);
          setIsLoading(false);
          return;
        }
        setError(errorMessage || copy.signInAfterVerificationFailed);
        toast.error(errorMessage || copy.signInFailed);
        setIsLoading(false);
        return;
      }

      clearPendingAuthPassword();
      setPendingPassword('');
      handedOff = pushAuthHandoff(
        router,
        `/auth/signing-in?${buildSigningInParams().toString()}`
      );
    } catch (error) {
      const { error: errorMessage } = handleAuthError(error);
      setError(errorMessage || copy.verificationFailed);
      toast.error(errorMessage || copy.verificationFailed);
      console.error('[auth:client] signup otpSubmit error', { error });
    } finally {
      // A handoff keeps the button pending until the page unmounts (#5559).
      if (!handedOff) {
        setIsLoading(false);
        setPendingAction(null);
      }
    }
  };

  const handleResendOtp = async () => {
    setIsLoading(true);
    setPendingAction('resend');
    setError(null);

    try {
      // Resend to the exact address the account was created with
      const normalizedEmail = normalizeSignUpEmail(pendingEmail);

      const result = await authClient.emailOtp.sendVerificationOtp({
        email: normalizedEmail,
        type: 'email-verification',
      });

      if (result.error) {
        const { error: errorMessage } = handleAuthError(result.error);
        setError(errorMessage || copy.failedToResendCodeShort);
        toast.error(errorMessage || copy.failedToResendCodeShort);
      } else {
        toast.success(copy.verificationCodeResentSuccess);
        setOtpCode('');
      }
    } catch (error) {
      const { error: errorMessage } = handleAuthError(error);
      setError(errorMessage || copy.failedToResendCodeShort);
      toast.error(errorMessage || copy.failedToResendCodeShort);
    } finally {
      setIsLoading(false);
      setPendingAction(null);
    }
  };

  const handleGoogleSignUp = async () => {
    setIsLoading(true);
    setPendingAction('google');
    setError(null);
    form.clearErrors();

    try {
      // Store invitation token if present
      if (inviteToken) {
        localStorage.setItem('pendingInvitation', inviteToken);
      }

      // Use returnTo if available, otherwise redirect to signing-in page.
      // The `signupFlow=1` param (set by buildSigningInParams) is the signal
      // the signing-in page uses to route fresh signups into onboarding.
      const returnTo = searchParams.get('returnTo');
      const callbackParams = buildSigningInParams();
      if (returnTo) {
        callbackParams.set('returnTo', returnTo);
      }
      if (signupPage) {
        clearPendingSignupPageToken();
        const { token } = await issuePendingSignupPageToken(signupPage.slug);
        callbackParams.set('signupToken', token);
      }
      const callbackURL = callbackParams.toString()
        ? `/auth/signing-in?${callbackParams.toString()}`
        : '/auth/signing-in';
      const error = await startGoogleSignIn({
        returnTo,
        locale: selectedLanguage,
        callbackURL,
      });

      if (error) {
        const { error: errorMessage } = handleAuthError(error);
        setError(errorMessage || copy.googleSignInFailed);
        toast.error(errorMessage || copy.googleSignInFailed);
      }
    } catch (error) {
      const { error: errorMessage } = handleAuthError(error);
      setError(errorMessage || copy.googleSignInFailed);
      if (errorMessage) {
        toast.error(errorMessage);
      }
    } finally {
      setIsLoading(false);
      setPendingAction(null);
    }
  };
  return {
    handleSubmit,
    handleOtpVerification,
    handleResendOtp,
    handleGoogleSignUp,
  };
}
